import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  FormGroup,
  Label,
  FormFeedback,
  Form,
} from 'reactstrap';
import { alertError, alertSuccess } from '../../../helpers/errorHandling';
import InnerLoader from '../../../Components/Common/InnerLoader';
import { PROMOTION_REPORT_GROUP_BY_OPTIONS } from '../../../constants';
import { useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import {
  createOrUpdateAnalytics,
  createOrUpdateAnalyticsChart,
} from '../../../actions/analytics/index';
import Select from 'react-select';
import QueryBuilderApp from '../../Promotion/QueryBuilderApp';
import { useSelector, useDispatch } from 'react-redux';

const CreateAnalyticsChartForm = ({
  submitHandler = () => {},
  closeHandler = () => {},
  reportConfigForEdit = null,
  pageId = null,
  chart = null,
}) => {
  const { fields } = useSelector((state) => state.promotions || {});
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const validationSchema = Yup.object().shape({
    _id: Yup.string().optional(),
    title: Yup.string()
      .required('Chart Title is required')
      .min(2, 'Chart Title must be at least 2 characters'),
    filterRule: Yup.object().optional(),
    groupBy: Yup.array().of(Yup.string()).optional(),
    chartType: Yup.string().required('Chart type is required'),
    xAxisLabel: Yup.string().optional(),
    yAxisLabel: Yup.string().optional(),
    xField: Yup.string().optional(),
    yField: Yup.string().optional(),
    nameField: Yup.string().when('chartType', (chartType, schema) =>
      chartType === 'pie'
        ? schema.required('Name field is required for pie chart')
        : schema
    ),
    valueField: Yup.string().when('chartType', (chartType, schema) =>
      chartType === 'pie'
        ? schema.required('Value field is required for pie chart')
        : schema
    ),
  });

  const initialValues = {
    title: '',
    filterRule: {
      combinator: 'and',
      rules: [],
    },
    groupBy: [],
    chartType: 'line',
    // Only for line and area charts
    xAxisLabel: '',
    yAxisLabel: '',
    xField: '',
    yField: '',
    nameField: '',
    valueField: '',
  };

  useEffect(() => {
    if (chart) {
      setValues({
        ...initialValues,
        filterRule: chart.filterRule || initialValues.filterRule,
        groupBy: chart.groupBy || [],
        chartType: chart.chartType || 'line',
        xAxisLabel: chart.xAxisLabel || '',
        yAxisLabel: chart.yAxisLabel || '',
        xField: chart.xField || '',
        yField: chart.yField || '',
        nameField: chart.nameField || '',
        valueField: chart.valueField || '',
        _id: chart._id || null,
        title: chart.title || '',
      });
    }
  }, [chart]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        const res = await createOrUpdateAnalyticsChart(
          {
            ...values,
            pageId: pageId ? pageId : null,
          },
          dispatch
        );

        if (res?._id) {
          alertSuccess('successfully created/updated');
          submitHandler();
        } else {
          alertError({ message: 'Failed, Please try again later' });
        }

        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      } catch (error) {
        setIsLoading(false);
        alertError({ message: 'Failed!,  Please try again later' });
        console.log(error);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setValues,
    values,
    isSubmitting,
    setFieldValue,
  } = formik;

  console.log(values, 'values');

  return (
    <>
      <FormikProvider value={formik}>
        <Form
          onSubmit={formik.handleSubmit}
          className="d-flex flex-row gap-3 flex-wrap"
        >
          <FormGroup className="col-5">
            <Label for="title">Chart Title</Label>
            <Input
              id="title"
              name="title"
              placeholder="Chart Title"
              type="text"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values?.title}
              invalid={!!errors?.title && touched?.title}
            />
            <FormFeedback>{touched?.title && errors?.title}</FormFeedback>
          </FormGroup>

          <FormGroup className="col-5">
            <Label for="chartType">Chart Type</Label>
            <Select
              id="chartType"
              name="chartType"
              options={[
                { value: 'line', label: 'Line' },
                { value: 'bar', label: 'Bar' },
                { value: 'pie', label: 'Pie' },
              ]}
              value={{
                value: values.chartType,
                label:
                  values.chartType?.charAt(0).toUpperCase() +
                  values.chartType?.slice(1),
              }}
              onChange={(option) => setFieldValue('chartType', option.value)}
            />
          </FormGroup>

          {/* Only show for line, area, and bar charts */}
          {['line', 'area', 'bar'].includes(values.chartType) && (
            <>
              <FormGroup className="col-5">
                <Label for="xAxisLabel">X-Axis Label</Label>
                <Input
                  id="xAxisLabel"
                  name="xAxisLabel"
                  type="text"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values?.xAxisLabel}
                />
              </FormGroup>

              <FormGroup className="col-5">
                <Label for="yAxisLabel">Y-Axis Label</Label>
                <Input
                  id="yAxisLabel"
                  name="yAxisLabel"
                  type="text"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values?.yAxisLabel}
                />
              </FormGroup>

              <FormGroup className="col-5">
                <Label for="xField">X Field</Label>
                <Input
                  id="xField"
                  name="xField"
                  placeholder="X Field (data key for x-axis)"
                  type="text"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values?.xField}
                />
              </FormGroup>

              <FormGroup className="col-5">
                <Label for="yField">Y Field</Label>
                <Input
                  id="yField"
                  name="yField"
                  placeholder="Y Field (data key for y-axis)"
                  type="text"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values?.yField}
                />
              </FormGroup>
            </>
          )}

          {values.chartType === 'pie' && (
            <>
              <FormGroup className="col-5">
                <Label for="nameField">Pie Name Field</Label>
                <Input
                  id="nameField"
                  name="nameField"
                  placeholder="Name field in data (e.g. label, name)"
                  type="text"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values?.nameField || ''}
                />
              </FormGroup>
              <FormGroup className="col-5">
                <Label for="valueField">Pie Value Field</Label>
                <Input
                  id="valueField"
                  name="valueField"
                  placeholder="Value field in data (e.g. value, count)"
                  type="text"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values?.valueField || ''}
                />
              </FormGroup>
            </>
          )}

          <FormGroup className="col-10">
            <Label for="groupBy">Group By</Label>
            <Select
              id="groupBy"
              name="groupBy"
              isMulti
              options={PROMOTION_REPORT_GROUP_BY_OPTIONS}
              value={PROMOTION_REPORT_GROUP_BY_OPTIONS.filter((option) =>
                (values?.groupBy || []).includes(option.value)
              )}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions
                  ? selectedOptions?.map((option) => option.value)
                  : [];
                setFieldValue('groupBy', selectedValues);
              }}
              onBlur={handleBlur}
              classNamePrefix="react-select"
              className={
                touched?.groupBy && errors?.groupBy ? 'is-invalid' : ''
              }
            />
            {touched?.groupBy && errors?.groupBy && (
              <div className="invalid-feedback d-block">{errors?.groupBy}</div>
            )}
          </FormGroup>

          <FormGroup className="col-10">
            <Label for="rules">Promotion Filter</Label>
            <QueryBuilderApp
              fieldGroups={['item']}
              data={{ ...values.filterRule }}
              updateQuery={(rule) => {
                setFieldValue('filterRule', rule);
              }}
            />
          </FormGroup>

          <div className="d-flex flex-row justify-content-end mt-3 w-100">
            <Button
              color="dark"
              size="sm"
              onClick={() => {
                closeHandler();
              }}
            >
              Close
            </Button>
            <Button color="primary" size="sm" type="submit" className="ms-2">
              {reportConfigForEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </Form>
      </FormikProvider>
      {isLoading ? <InnerLoader /> : null}
    </>
  );
};

export default CreateAnalyticsChartForm;
